import { type ZodSchema } from 'zod';
import {
  hasItemsSelected,
  type ItemId,
  type Tasks,
  type Workflow,
  type WorkflowData,
  type WorkflowItemData,
} from '../workflow/schemaTypes';

/**
 * Validates a specific item against a schema
 * @param itemId - ID of item to validate
 * @param schema - Zod schema to validate against
 * @param taskId - ID of task containing the schema
 * @returns boolean indicating if item passes validation
 */
export const validateItemTaskData = (
  itemData: WorkflowItemData | undefined,
  schema: ZodSchema,
  taskId: Tasks,
) => {
  if (!itemData || !(taskId in itemData)) {
    return false;
  }

  return schema.safeParse(itemData[taskId as keyof WorkflowItemData]).success;
};

/**
 * Determines if a specific task is complete based on its validation requirements
 * @param taskId - ID of the task to check
 * @param data - Current workflow data
 * @param config - Workflow configuration
 * @param itemId - Optional specific item ID to check (for item-scoped tasks)
 * @returns boolean indicating if the task is complete
 */
export const isTaskComplete = (
  taskId: Tasks,
  data: WorkflowData | null,
  config: Workflow | null,
  itemId: ItemId | undefined,
) => {
  if (!config || !data) {
    return false;
  }

  // Find the task configuration from all steps
  const thisTask = config.steps
    .flatMap((step) => step.tasks)
    .find((task) => task.id === taskId);

  if (!thisTask) return false;

  const { schema, scope } = thisTask;

  // For tasks that operate on individual items
  if (scope === 'item' && data.items) {
    // Task can't be complete if no items are selected
    if (!hasItemsSelected(data)) {
      return false;
    }

    // If specific item provided, validate just that item
    if (itemId) {
      return validateItemTaskData(data.items?.[itemId], schema, taskId);
    }

    // Otherwise validate all selected items
    return Object.keys(data.items || {}).every((itemId) =>
      validateItemTaskData(data.items?.[itemId], schema, taskId),
    );
  } else {
    // For tasks that operate on workflow-level data

    const taskData = data[taskId as keyof typeof data];
    return schema.safeParse(taskData).success;
  }
};
